import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, BellRing, Copy, Download, Edit3, Loader2, Mail, MessageCircle, Printer, ReceiptText, Send, Share2, Wallet } from "lucide-react";
import SendDocumentDialog from "@/components/SendDocumentDialog";
import { api, apiError, complianceErrors } from "@/lib/api";
import { inr, fmtDate, waNumber } from "@/lib/format";
import InvoiceDocument from "@/components/InvoiceDocument";
import StatusBadge from "@/components/StatusBadge";
import EInvoicePanel from "@/components/EInvoicePanel";
import { Button } from "@/components/ui/button";
import { useCanEdit } from "@/context/AuthContext";

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sending, setSending] = useState(null);

  // The PDF is generated server-side, so it needs an authenticated request —
  // a plain <a href> would arrive without the session cookie on some browsers.
  const downloadPdf = async (docNumber) => {
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(docNumber || "invoice").replace(/\//g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    }
    setPdfBusy(false);
  };
  const canEdit = useCanEdit();
  const [doc, setDoc] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/documents/${id}`);
    setDoc(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    setErrors([]);
    try {
      const res = await fn();
      toast.success(successMsg);
      await load();
      return res;
    } catch (e) {
      const details = complianceErrors(e);
      setErrors(details);
      toast.error(details.length ? "GST compliance check failed" : apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!doc) return <div className="text-sm text-slate-500">Loading…</div>;

  const shareUrl = doc.share_token ? `${window.location.origin}/v/${doc.share_token}` : null;

  const share = async () => {
    const res = await act(() => api.post(`/documents/${id}/share`), "Share link ready");
    const token = res?.data?.token || doc.share_token;
    if (token) {
      const url = `${window.location.origin}/v/${token}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success("Public link copied to clipboard");
    }
  };

  const whatsapp = async () => {
    let token = doc.share_token;
    if (!token) {
      const res = await act(() => api.post(`/documents/${id}/share`), "Share link ready");
      token = res?.data?.token;
    }
    const url = `${window.location.origin}/v/${token}`;
    const msg = `Hi ${doc.party_name}, please find Invoice ${doc.document_number} dated ${fmtDate(
      doc.document_date
    )} for ${inr(doc.totals.grand_total)} from ${doc.organization?.name}. View & download: ${url}${
      // Never ask for money on an invoice that is already settled — the
      // customer reads that as a chase, and they have paid.
      doc.organization?.upi_id && Number(doc.balance_due || 0) >= 0.01
        ? `. Pay via UPI: ${doc.organization.upi_id}`
        : ""
    }. Thank you!`;
    const phone = waNumber(doc.party_phone);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-5" data-testid="invoice-view-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight font-mono" data-testid="view-doc-number">
              {doc.document_number || "DRAFT"}
            </h1>
            <StatusBadge status={doc.status === "ISSUED" ? doc.payment_status : doc.status} testid="view-status" />
            {doc.is_financial_credit_note && <StatusBadge status="OVERDUE" label="Financial CN" />}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {doc.party_name} · {fmtDate(doc.document_date)} ·{" "}
            <span className="font-mono">{inr(doc.totals.grand_total)}</span>
            {doc.status === "ISSUED" && doc.balance_due > 0.01 && (
              <>
                {" "}· balance <span className="font-mono text-rose-700">{inr(doc.balance_due)}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {doc.status === "DRAFT" && canEdit && (
            <Button size="sm" disabled={busy} data-testid="issue-btn" onClick={() => act(() => api.post(`/documents/${id}/issue`), "Invoice issued")}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700">
              <Send size={15} /> Issue
            </Button>
          )}
          {canEdit && doc.status !== "CANCELLED" && doc.document_type !== "CREDIT_NOTE" && (
            <Button size="sm" variant="outline" data-testid="edit-btn" onClick={() => navigate(`/invoices/${id}/edit`)} className="gap-1.5">
              <Edit3 size={15} /> Edit
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={pdfBusy} data-testid="download-pdf-btn"
            onClick={() => downloadPdf(doc.document_number)} className="gap-1.5">
            {pdfBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} PDF
          </Button>
          {doc.status === "ISSUED" && (
            <>
              <Button size="sm" variant="outline" data-testid="share-btn" onClick={share} className="gap-1.5">
                <Share2 size={15} /> Share link
              </Button>
              <Button size="sm" variant="outline" data-testid="email-btn"
                onClick={() => setSending("SEND")} className="gap-1.5">
                <Mail size={15} /> Email
              </Button>
              <Button size="sm" variant="outline" data-testid="whatsapp-btn" onClick={whatsapp} className="gap-1.5">
                <MessageCircle size={15} /> WhatsApp
              </Button>
              <Button size="sm" variant="outline" data-testid="print-btn" onClick={() => window.print()} className="gap-1.5">
                <Printer size={15} /> Print
              </Button>
            </>
          )}
          {doc.status === "ISSUED" && canEdit && doc.document_type !== "CREDIT_NOTE" && (
            <>
              {doc.balance_due > 0.01 && (
                <Button size="sm" variant="outline" data-testid="remind-btn"
                  onClick={() => setSending("REMIND")} className="gap-1.5">
                  <BellRing size={15} /> Send reminder
                </Button>
              )}
              <Button size="sm" variant="outline" data-testid="record-payment-btn"
                onClick={() => navigate(`/payments?new=1&party_id=${doc.party_id}&amount=${doc.balance_due}`)} className="gap-1.5">
                <Wallet size={15} /> Record payment
              </Button>
              <Button size="sm" variant="outline" data-testid="credit-note-btn"
                onClick={async () => {
                  const res = await act(() => api.post(`/documents/${id}/credit-note`), "Credit note drafted");
                  if (res?.data?.id) navigate(`/invoices/${res.data.id}`);
                }}
                className="gap-1.5">
                <ReceiptText size={15} /> Credit note
              </Button>
            </>
          )}
          {canEdit && doc.status !== "CANCELLED" && doc.status !== "VOID" && (
            <Button size="sm" variant="outline" data-testid="void-btn"
              onClick={() => act(() => api.post(`/documents/${id}/void`, { reason: "Cancelled by user" }), "Document cancelled")}
              className="gap-1.5 text-rose-700 hover:text-rose-800">
              <Ban size={15} /> Cancel
            </Button>
          )}
        </div>
      </div>

      {!!errors.length && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4" data-testid="view-compliance-errors">
          <div className="text-xs font-semibold uppercase tracking-widest text-rose-800">Cannot issue yet</div>
          <ul className="mt-2 space-y-1 text-xs text-rose-700 list-disc pl-4">
            {errors.map((e, i) => (
              <li key={i}><span className="font-mono">{e.field}</span> — {e.message}</li>
            ))}
          </ul>
        </div>
      )}

      {shareUrl && (
        <div className="rounded-md border border-slate-200 bg-white px-4 py-2.5 flex items-center justify-between gap-3 text-xs" data-testid="share-link-row">
          <span className="font-mono truncate text-slate-600">{shareUrl}</span>
          <button className="flex items-center gap-1 text-blue-700 hover:text-blue-900 transition-colors duration-150"
            onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Copied"); }}>
            <Copy size={13} /> Copy
          </button>
        </div>
      )}

      <div className="print:hidden empty:hidden">
        <EInvoicePanel docId={id} canEdit={canEdit} onChange={load} />
      </div>

      <InvoiceDocument doc={doc} org={doc.organization} />

      {!!doc.payments?.length && (
        <div className="rounded-md border border-slate-200 bg-white" data-testid="doc-payments">
          <div className="px-5 py-3 border-b border-slate-200 text-sm font-semibold">Payments applied</div>
          <table className="w-full text-xs">
            <tbody className="divide-y divide-slate-100">
              {doc.payments.map((p, i) => (
                <tr key={i}>
                  <td className="px-5 py-2.5 font-mono">{p.payment_number}</td>
                  <td className="px-3 py-2.5 text-slate-600">{fmtDate(p.payment_date)}</td>
                  <td className="px-3 py-2.5 text-slate-600">{p.payment_mode.replace(/_/g, " ")}</td>
                  <td className="px-5 py-2.5 text-right font-mono font-semibold">{inr(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SendDocumentDialog mode={sending} doc={doc} onClose={() => setSending(null)} />
    </div>
  );
}
